import {
  IPaginate,
  IUssdCallback,
  IUssdCallbackBody,
  IUssdCallbackUpdate,
  IUssdHit,
  IUssdHitStats,
  IUssdSession,
  RootState,
} from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { UssdAdminService } from '@/services/ussd-admin.service';

const state = {
  callbacks: [] as IUssdCallback[],
  callbacksPagination: { total: 0, itemsPerPage: 50, page: 1, pages: 0 } as IPaginate,
  sessions: [] as IUssdSession[],
  hits: [] as IUssdHit[],
  hitsPagination: { total: 0, itemsPerPage: 50, page: 1, pages: 0 } as IPaginate,
  hitsTotal: 0,
  hitStats: null as IUssdHitStats | null,
};

type UssdAdminState = typeof state;

const mutations: MutationTree<UssdAdminState> = {
  UPDATE_CALLBACKS(state, payload: IUssdCallback[]) {
    state.callbacks = payload;
  },
  UPDATE_CALLBACKS_PAGINATION(state, payload: IPaginate) {
    state.callbacksPagination = payload;
  },
  UPDATE_SESSIONS(state, payload: IUssdSession[]) {
    state.sessions = payload;
  },
  UPDATE_HITS(state, payload: IUssdHit[]) {
    state.hits = payload;
  },
  UPDATE_HITS_PAGINATION(state, payload: IPaginate) {
    state.hitsPagination = payload;
  },
  UPDATE_HITS_TOTAL(state, payload: number) {
    state.hitsTotal = payload;
  },
  UPDATE_HIT_STATS(state, payload: IUssdHitStats) {
    state.hitStats = payload;
  },
};

const reportError = (dispatch: any, e: any) => {
  if (isNetworkError(e)) {
    dispatch('snackBarMessage', e?.message, { root: true });
  } else {
    dispatch('snackBarMessage', e.response?.data?.message ?? e?.message, { root: true });
  }
  dispatch('snackBarVisibility', true, { root: true });
};

const actions: ActionTree<UssdAdminState, RootState> = {
  async fetchCallbacks({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await UssdAdminService.listCallbacks(query);
      const { data, meta } = response$.data?.data ?? {};
      commit('UPDATE_CALLBACKS', data ?? []);
      commit('UPDATE_CALLBACKS_PAGINATION', {
        page: meta.page,
        pages: meta.totalPages,
        total: meta.total,
        itemsPerPage: meta.limit,
      });
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isPageLoading', false, { root: true });
    }
  },

  async createCallback({ dispatch }, payload: { body: IUssdCallbackBody; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await UssdAdminService.createCallback(payload.body);
      dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
      dispatch('snackBarMessage', 'Callback registered successfully', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchCallbacks', payload.query);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isLoading', false, { root: true });
    }
  },

  async updateCallback({ dispatch }, payload: { id: string; body: IUssdCallbackUpdate; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await UssdAdminService.updateCallback(payload.id, payload.body);
      dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
      dispatch('snackBarMessage', 'Callback updated — new sessions use it immediately', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchCallbacks', payload.query);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isLoading', false, { root: true });
    }
  },

  async updateCallbackStatus({ dispatch }, payload: { id: string; status: string; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await UssdAdminService.updateCallbackStatus(payload.id, payload.status);
      dispatch('snackBarMessage', `Callback ${payload.status === 'ACTIVE' ? 'activated' : 'deactivated'}`, {
        root: true,
      });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchCallbacks', payload.query);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isLoading', false, { root: true });
    }
  },

  async fetchSessions({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await UssdAdminService.listSessions(query);
      commit('UPDATE_SESSIONS', response$.data?.data?.data ?? []);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isPageLoading', false, { root: true });
    }
  },

  async fetchHits({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await UssdAdminService.listHits(query);
      const { data, meta } = response$.data?.data ?? {};
      commit('UPDATE_HITS', data ?? []);
      commit('UPDATE_HITS_TOTAL', meta?.totalHits ?? 0);
      commit('UPDATE_HITS_PAGINATION', {
        page: meta?.page,
        pages: meta?.totalPages,
        total: meta?.total,
        itemsPerPage: meta?.limit,
      });
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isPageLoading', false, { root: true });
    }
  },

  async fetchHitStats({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await UssdAdminService.hitStats(query);
      commit('UPDATE_HIT_STATS', response$.data?.data ?? null);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isPageLoading', false, { root: true });
    }
  },

  // The API streams the file, so trigger a browser download from the blob.
  async exportHits({ dispatch }, query: string) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await UssdAdminService.exportHits(query);
      const disposition = response$.headers?.['content-disposition'] ?? '';
      const suggested = /filename="?([^"]+)"?/.exec(disposition)?.[1];
      const blob = new Blob([response$.data]);
      const anchor = document.createElement('a');
      anchor.download = suggested ?? 'ussd-hits.csv';
      anchor.href = URL.createObjectURL(blob);
      anchor.click();
      URL.revokeObjectURL(anchor.href);

      if (response$.headers?.['x-truncated'] === 'true') {
        dispatch('snackBarMessage', `Export capped at ${response$.headers['x-row-count']} rows`, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
      }
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isLoading', false, { root: true });
    }
  },

  async releaseSession({ dispatch }, payload: { sessionId: string; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await UssdAdminService.releaseSession(payload.sessionId);
      dispatch('snackBarMessage', 'Session released', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchSessions', payload.query);
    } catch (e) {
      reportError(dispatch, e);
    } finally {
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<UssdAdminState, RootState> = {
  getCallbacks: state => state.callbacks,
  getCallbacksPagination: state => state.callbacksPagination,
  getSessions: state => state.sessions,
  getHits: state => state.hits,
  getHitsPagination: state => state.hitsPagination,
  getHitsTotal: state => state.hitsTotal,
  getHitStats: state => state.hitStats,
};

export const ussdAdmin: Module<UssdAdminState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
